import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * The assistant panel's open state, shared so any page can open it: the floating button, /staff/assistant (with
 * ?task=<id> from a "task is done" notification, which opens that task) and the quote page's "Explain these with the
 * assistant", which opens a new chat on the quote with the question already asked.
 */

export type PageRef = { type: 'project' | 'quote' | 'design'; id: number };
export type OpenRequest = {
  /** Start a new chat on this page record and send this question at once. */
  ask?: string;
  context?: PageRef;
  /** Open this background task, as a portal notification does. */
  taskId?: number;
  /** A counter so the same request twice still opens a new chat. */
  key: number;
};

type AssistantState = {
  isOpen: boolean;
  request: OpenRequest | null;
  open: (opts?: { ask?: string; context?: PageRef; taskId?: number }) => void;
  close: () => void;
  /** The panel calls this once it has started the requested chat. */
  consume: () => void;
};

const Ctx = createContext<AssistantState | null>(null);

export function AssistantProvider({ children }: { children: ReactNode }) {
  const [isOpen, setOpen] = useState(false);
  const [request, setRequest] = useState<OpenRequest | null>(null);
  const open = useCallback((opts?: { ask?: string; context?: PageRef; taskId?: number }) => {
    setOpen(true);
    if (opts?.ask || opts?.taskId) setRequest({ ask: opts.ask, context: opts.context, taskId: opts.taskId, key: Date.now() });
  }, []);
  const close = useCallback(() => setOpen(false), []);
  const consume = useCallback(() => setRequest(null), []);
  const value = useMemo(() => ({ isOpen, request, open, close, consume }), [isOpen, request, open, close, consume]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAssistant() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAssistant must be used inside AssistantProvider');
  return ctx;
}

/** The record a portal address shows, for the page chip. */
export function pageRefFor(pathname: string): PageRef | null {
  let m = /^\/projects\/(\d+)\/designs\/(\d+)(\/|$)/.exec(pathname);
  if (m) return { type: 'design', id: Number(m[2]) };
  m = /^\/projects\/(\d+)(\/|$)/.exec(pathname);
  if (m) return { type: 'project', id: Number(m[1]) };
  m = /^\/quotes\/(\d+)(\/|$)/.exec(pathname);
  if (m) return { type: 'quote', id: Number(m[1]) };
  return null;
}
